import { useEffect, useState } from 'react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';
import { FormField } from './FormField.tsx';
import { Select } from './Select.tsx';
import { Textarea } from './Textarea.tsx';

interface CheckIn {
  id: string;
  organizationId: string;
  organizationName: string;
  scheduledAt: string;
  durationMinutes: number;
  title: string;
  meetingUrl: string | null;
  assignedAgentId: string | null;
  assignedAgentName: string | null;
  status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
  firefliesUrl: string | null;
  notes: string | null;
  attendeeEmails: string[];
  gcalEventId: string | null;
  seriesId: string | null;
  recurrence: 'weekly' | 'biweekly' | 'monthly' | null;
  completedAt: string | null;
}

type Recurrence = 'weekly' | 'biweekly' | 'monthly' | null;

interface AgentSummary {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'agent';
}

interface OrgContact {
  id: string;
  name: string | null;
  email: string;
  isOrgAdmin?: boolean;
  isOrgBilling?: boolean;
  isRequester?: boolean;
}

interface OrgLite {
  id: string;
  name: string;
  checkinCadence: 'weekly' | 'biweekly' | 'monthly' | 'none' | null;
  checkinDayOfWeek: string | null;
  checkinTime: string | null;
}

interface Props {
  org: OrgLite;
}

const CADENCE_LABELS: Record<NonNullable<OrgLite['checkinCadence']>, string> = {
  weekly: 'Weekly',
  biweekly: 'Biweekly',
  monthly: 'Monthly',
  none: 'No scheduled check-ins',
};

function defaultNextDate(cadence: OrgLite['checkinCadence']): string {
  const days = cadence === 'weekly' ? 7 : cadence === 'biweekly' ? 14 : cadence === 'monthly' ? 30 : 14;
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setMinutes(0, 0, 0);
  d.setHours(10);
  // Format as YYYY-MM-DDTHH:mm for datetime-local input.
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CheckInsPanel({ org }: Props): React.ReactElement {
  const [checkIns, setCheckIns] = useState<CheckIn[] | null>(null);
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [orgContacts, setOrgContacts] = useState<OrgContact[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  // schedule form
  const [showForm, setShowForm] = useState(false);
  const [scheduledAt, setScheduledAt] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(15);
  const [title, setTitle] = useState('');
  const [assignedAgentId, setAssignedAgentId] = useState('');
  const [recurrence, setRecurrence] = useState<Recurrence>(null);
  // Two attendee channels: emails picked from the org's contact list
  // (Set of email addresses), and a free-text overflow for non-contact
  // people (T3 staff, external partners).
  const [pickedContactEmails, setPickedContactEmails] = useState<Set<string>>(new Set());
  const [attendeesText, setAttendeesText] = useState('');
  const [busy, setBusy] = useState(false);

  // edit form (for an existing check-in)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState<CheckIn['status']>('scheduled');
  const [editNotes, setEditNotes] = useState('');
  const [editPickedContactEmails, setEditPickedContactEmails] = useState<Set<string>>(new Set());
  const [editAttendeesText, setEditAttendeesText] = useState('');
  const [editBusy, setEditBusy] = useState(false);

  async function load() {
    try {
      const r = await apiFetch<{ checkIns: CheckIn[] }>(
        `/api/check-ins?organizationId=${encodeURIComponent(org.id)}`,
      );
      setCheckIns(r.checkIns);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load check-ins.');
    }
  }

  useEffect(() => {
    void load();
    apiFetch<{ agents: AgentSummary[] }>('/api/agents')
      .then((r) => setAgents(r.agents))
      .catch(() => undefined);
    apiFetch<{ contacts: OrgContact[] }>(`/api/organizations/${encodeURIComponent(org.id)}/contacts`)
      .then((r) => setOrgContacts(r.contacts))
      .catch(() => setOrgContacts([]));
  }, [org.id]);

  // Split a free-form list back into "picked contacts" (those whose email
  // is in the org's contact list) and "free-text" (everything else). Used
  // when opening the edit form for an existing check-in.
  function splitAttendees(emails: string[]): { picked: Set<string>; free: string } {
    const contactSet = new Set(orgContacts.map((c) => c.email.toLowerCase()));
    const picked = new Set<string>();
    const free: string[] = [];
    for (const e of emails) {
      if (contactSet.has(e.toLowerCase())) picked.add(e.toLowerCase());
      else free.push(e);
    }
    return { picked, free: free.join(', ') };
  }

  function combineAttendees(picked: Set<string>, freeText: string): string[] {
    const fromText = parseEmails(freeText);
    const seen = new Set<string>();
    const out: string[] = [];
    for (const e of [...picked, ...fromText]) {
      const key = e.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(e);
      }
    }
    return out;
  }

  function openForm() {
    setScheduledAt(defaultNextDate(org.checkinCadence));
    setDurationMinutes(30);
    setTitle(`Check-in - ${org.name}`);
    setAssignedAgentId('');
    setAttendeesText('');
    // Pre-pick the org admin(s) since they're the typical default invitees.
    const defaults = new Set<string>();
    for (const c of orgContacts) {
      if (c.isOrgAdmin) defaults.add(c.email.toLowerCase());
    }
    setPickedContactEmails(defaults);
    // Pre-select the org's configured cadence so the form matches the
    // intended rhythm — agent can override per-schedule.
    const cadence = org.checkinCadence;
    setRecurrence(
      cadence === 'weekly' || cadence === 'biweekly' || cadence === 'monthly' ? cadence : null,
    );
    setShowForm(true);
  }

  function parseEmails(text: string): string[] {
    return text
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter((s) => s.includes('@'));
  }

  async function submitSchedule(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setWarning(null);
    try {
      const r = await apiFetch<{ inviteWarning?: string | null }>('/api/check-ins', {
        method: 'POST',
        json: {
          organizationId: org.id,
          scheduledAt: new Date(scheduledAt).toISOString(),
          durationMinutes,
          title: title.trim() || `Check-in - ${org.name}`,
          assignedAgentId: assignedAgentId || null,
          recurrence,
          attendeeEmails: combineAttendees(pickedContactEmails, attendeesText),
        },
      });
      setShowForm(false);
      if (r?.inviteWarning) setWarning(r.inviteWarning);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to schedule.');
    } finally {
      setBusy(false);
    }
  }

  function openEdit(c: CheckIn) {
    setEditingId(c.id);
    setEditStatus(c.status);
    setEditNotes(c.notes ?? '');
    const split = splitAttendees(c.attendeeEmails);
    setEditPickedContactEmails(split.picked);
    setEditAttendeesText(split.free);
  }

  async function submitEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setEditBusy(true);
    setError(null);
    setWarning(null);
    try {
      const r = await apiFetch<{ inviteWarning?: string | null }>(`/api/check-ins/${editingId}`, {
        method: 'PATCH',
        json: {
          status: editStatus,
          notes: editNotes.trim() || null,
          attendeeEmails: combineAttendees(editPickedContactEmails, editAttendeesText),
        },
      });
      setEditingId(null);
      if (r?.inviteWarning) setWarning(r.inviteWarning);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to save.');
    } finally {
      setEditBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm('Delete this check-in?')) return;
    try {
      await apiFetch(`/api/check-ins/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Delete failed.');
    }
  }

  async function cancelSeriesFromHere(c: CheckIn) {
    if (!c.seriesId) return;
    if (
      !window.confirm(
        `Cancel this check-in and all future occurrences in the recurring series? Past occurrences are kept.`,
      )
    )
      return;
    try {
      await apiFetch(`/api/check-ins/${c.id}?cancelFutureSeries=true`, { method: 'DELETE' });
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Cancel failed.');
    }
  }

  const upcoming = (checkIns ?? []).filter((c) => c.status === 'scheduled' && new Date(c.scheduledAt) >= new Date());
  const past = (checkIns ?? []).filter((c) => !(c.status === 'scheduled' && new Date(c.scheduledAt) >= new Date()));

  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Check-ins</h2>
          <p className="text-xs text-[var(--color-muted)]">
            Cadence: {org.checkinCadence ? CADENCE_LABELS[org.checkinCadence] : 'not set'}
            {org.checkinCadence !== 'none' &&
              org.checkinDayOfWeek &&
              ` · usually ${org.checkinDayOfWeek}${org.checkinTime ? ` at ${org.checkinTime}` : ''}`}
          </p>
        </div>
        <Button variant="outline" onClick={openForm}>
          + Schedule check-in
        </Button>
      </div>

      {error && (
        <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>
      )}
      {warning && (
        <div className="mt-3 rounded-[var(--radius-button)] border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <div className="font-semibold">Check-in saved, but invites didn't go out.</div>
          <div className="mt-1">{warning}</div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submitSchedule} className="mt-4 space-y-3 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-[var(--color-surface)]/40 p-4">
          <FormField
            label="Title"
            name="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={500}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label="Scheduled at"
              name="scheduledAt"
              type="datetime-local"
              required
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
            />
            <FormField
              label="Duration (minutes)"
              name="duration"
              type="number"
              min={5}
              step={5}
              value={String(durationMinutes)}
              onChange={(e) => setDurationMinutes(Math.max(5, Number(e.target.value) || 15))}
            />
          </div>
          <p className="text-xs text-[var(--color-muted)]">
            A Teams meeting URL is created automatically and added to the check-in. If Microsoft
            isn't connected, the check-in saves without a URL — connect under Admin → Microsoft.
          </p>
          <Select
            label="Assigned agent"
            value={assignedAgentId}
            onChange={(e) => setAssignedAgentId(e.target.value)}
          >
            <option value="">Me (default)</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({a.email})
              </option>
            ))}
          </Select>
          <ContactPicker
            label={`Invite from ${org.name}`}
            contacts={orgContacts}
            picked={pickedContactEmails}
            onChange={setPickedContactEmails}
          />
          <FormField
            label="Other attendees (emails)"
            name="attendees"
            placeholder="external-partner@example.com"
            value={attendeesText}
            onChange={(e) => setAttendeesText(e.target.value)}
            hint="Optional. For people outside the org's contact list (T3 staff, external partners). Comma- or space-separated."
          />
          <Select
            label="Recurrence"
            value={recurrence ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setRecurrence(
                v === 'weekly' || v === 'biweekly' || v === 'monthly' ? v : null,
              );
            }}
          >
            <option value="">One-time</option>
            <option value="weekly">Weekly (next 8 weeks)</option>
            <option value="biweekly">Biweekly (next 12 weeks)</option>
            <option value="monthly">Monthly (next 6 months)</option>
          </Select>
          {recurrence && (
            <p className="text-xs text-[var(--color-muted)]">
              We'll create the first occurrence on the date above plus future occurrences at this
              cadence. Each is its own row — edit or cancel any of them individually, or use
              "Cancel series from here" to stop the cadence going forward.
            </p>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !scheduledAt}>
              {busy ? 'Scheduling…' : 'Schedule'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      <div className="mt-4 space-y-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">Upcoming</div>
          {upcoming.length === 0 ? (
            <p className="mt-2 text-sm text-[var(--color-muted)]">None scheduled.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {upcoming.map((c) => (
                <CheckInRow
                  key={c.id}
                  checkIn={c}
                  editing={editingId === c.id}
                  editStatus={editStatus}
                  editNotes={editNotes}
                  editAttendeesText={editAttendeesText}
                  editPickedContactEmails={editPickedContactEmails}
                  orgContacts={orgContacts}
                  orgName={org.name}
                  editBusy={editBusy}
                  onEditOpen={() => openEdit(c)}
                  onEditCancel={() => setEditingId(null)}
                  onEditSubmit={submitEdit}
                  onEditStatus={setEditStatus}
                  onEditNotes={setEditNotes}
                  onEditAttendees={setEditAttendeesText}
                  onEditPickedContacts={setEditPickedContactEmails}
                  onDelete={() => remove(c.id)}
                  onCancelSeriesFromHere={() => cancelSeriesFromHere(c)}
                />
              ))}
            </ul>
          )}
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">Past / cancelled</div>
          {past.length === 0 ? (
            <p className="mt-2 text-sm text-[var(--color-muted)]">No history yet.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {past.slice(0, 12).map((c) => (
                <CheckInRow
                  key={c.id}
                  checkIn={c}
                  editing={editingId === c.id}
                  editStatus={editStatus}
                  editNotes={editNotes}
                  editAttendeesText={editAttendeesText}
                  editPickedContactEmails={editPickedContactEmails}
                  orgContacts={orgContacts}
                  orgName={org.name}
                  editBusy={editBusy}
                  onEditOpen={() => openEdit(c)}
                  onEditCancel={() => setEditingId(null)}
                  onEditSubmit={submitEdit}
                  onEditStatus={setEditStatus}
                  onEditNotes={setEditNotes}
                  onEditAttendees={setEditAttendeesText}
                  onEditPickedContacts={setEditPickedContactEmails}
                  onDelete={() => remove(c.id)}
                  onCancelSeriesFromHere={() => cancelSeriesFromHere(c)}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}

function CheckInRow({
  checkIn,
  editing,
  editStatus,
  editNotes,
  editAttendeesText,
  editPickedContactEmails,
  orgContacts,
  orgName,
  editBusy,
  onEditOpen,
  onEditCancel,
  onEditSubmit,
  onEditStatus,
  onEditNotes,
  onEditAttendees,
  onEditPickedContacts,
  onDelete,
  onCancelSeriesFromHere,
}: {
  checkIn: CheckIn;
  editing: boolean;
  editStatus: CheckIn['status'];
  editNotes: string;
  editAttendeesText: string;
  editPickedContactEmails: Set<string>;
  orgContacts: OrgContact[];
  orgName: string;
  editBusy: boolean;
  onEditOpen: () => void;
  onEditCancel: () => void;
  onEditSubmit: (e: React.FormEvent) => void;
  onEditStatus: (s: CheckIn['status']) => void;
  onEditNotes: (s: string) => void;
  onEditAttendees: (s: string) => void;
  onEditPickedContacts: (s: Set<string>) => void;
  onDelete: () => void;
  onCancelSeriesFromHere: () => void;
}): React.ReactElement {
  const dt = new Date(checkIn.scheduledAt);
  const dateLabel = dt.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeLabel = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <li className="rounded-[var(--radius-button)] border border-[var(--color-border)] p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3 text-sm">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-[var(--color-header)]">{checkIn.title}</span>
            {checkIn.recurrence && (
              <span
                className="rounded-full bg-[var(--color-t3-blue)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-t3-blue)]"
                title="Part of a recurring series"
              >
                ↻ {checkIn.recurrence}
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--color-muted)]">
            {dateLabel} · {timeLabel} · {checkIn.durationMinutes}m
            {checkIn.assignedAgentName ? ` · ${checkIn.assignedAgentName}` : ''}
          </div>
          {checkIn.attendeeEmails.length > 0 && (
            <div className="mt-1 text-xs text-[var(--color-muted)]">
              {checkIn.gcalEventId ? (
                <>
                  {checkIn.attendeeEmails.length} invited:{' '}
                </>
              ) : (
                <span className="font-semibold text-amber-700">
                  {checkIn.attendeeEmails.length} listed, no calendar invite sent:{' '}
                </span>
              )}
              {checkIn.attendeeEmails.slice(0, 4).join(', ')}
              {checkIn.attendeeEmails.length > 4
                ? ` +${checkIn.attendeeEmails.length - 4} more`
                : ''}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={
              'rounded-full px-2 py-0.5 text-xs ' +
              (checkIn.status === 'scheduled'
                ? 'bg-emerald-50 text-emerald-700'
                : checkIn.status === 'completed'
                  ? 'bg-[var(--color-surface)] text-[var(--color-muted)]'
                  : checkIn.status === 'no_show'
                    ? 'bg-amber-50 text-amber-700'
                    : 'bg-red-50 text-red-700')
            }
          >
            {checkIn.status.replace('_', ' ')}
          </span>
          {checkIn.meetingUrl && (
            <a
              href={checkIn.meetingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-[var(--color-t3-blue)] hover:underline"
            >
              Teams →
            </a>
          )}
          {checkIn.firefliesUrl && (
            <a
              href={checkIn.firefliesUrl}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-semibold text-[var(--color-t3-purple)] hover:underline"
            >
              Fireflies →
            </a>
          )}
          {!editing && (
            <button
              type="button"
              onClick={onEditOpen}
              className="text-xs text-[var(--color-muted)] hover:text-[var(--color-header)] hover:underline"
            >
              Edit
            </button>
          )}
          {!editing && (
            <button
              type="button"
              onClick={onDelete}
              className="text-xs text-[var(--color-danger)] hover:underline"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {editing && (
        <form onSubmit={onEditSubmit} className="mt-3 space-y-3 border-t border-[var(--color-border)] pt-3">
          <Select
            label="Status"
            value={editStatus}
            onChange={(e) => onEditStatus(e.target.value as CheckIn['status'])}
          >
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No-show</option>
          </Select>
          <p className="text-xs text-[var(--color-muted)]">
            Teams meeting URL is auto-created when scheduling; Fireflies URL auto-attaches after the
            recording is processed. Use the links above to open them.
          </p>
          <ContactPicker
            label={`Invite from ${orgName}`}
            contacts={orgContacts}
            picked={editPickedContactEmails}
            onChange={onEditPickedContacts}
          />
          <FormField
            label="Other attendees (emails)"
            name="editAttendees"
            placeholder="external-partner@example.com"
            value={editAttendeesText}
            onChange={(e) => onEditAttendees(e.target.value)}
            hint="For people outside the org's contact list. Saving syncs the Google Calendar event — additions get a real invite, removals get a cancellation."
          />
          <Textarea
            label="Notes"
            name="notes"
            rows={4}
            value={editNotes}
            onChange={(e) => onEditNotes(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" disabled={editBusy}>
              {editBusy ? 'Saving…' : 'Save'}
            </Button>
            <Button type="button" variant="outline" onClick={onEditCancel}>
              Cancel
            </Button>
            {checkIn.seriesId && (
              <button
                type="button"
                onClick={onCancelSeriesFromHere}
                className="ml-auto text-xs text-[var(--color-danger)] hover:underline"
                title="Cancel this and all later occurrences in the recurring series"
              >
                Cancel series from here →
              </button>
            )}
          </div>
        </form>
      )}
    </li>
  );
}

// Org-contact checkbox picker. The "picked" set is keyed by lowercase
// email so toggling matches regardless of how the email was cased on
// either side (DB vs. user input).
function ContactPicker({
  label,
  contacts,
  picked,
  onChange,
}: {
  label: string;
  contacts: OrgContact[];
  picked: Set<string>;
  onChange: (next: Set<string>) => void;
}): React.ReactElement {
  function toggle(email: string) {
    const key = email.toLowerCase();
    const next = new Set(picked);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  }

  if (contacts.length === 0) {
    return (
      <div>
        <label className="block text-xs font-medium text-[var(--color-header)]">{label}</label>
        <p className="mt-1 text-xs italic text-[var(--color-muted)]">
          No contacts on this organization yet. Add some on the org's Contacts panel, then come back.
        </p>
      </div>
    );
  }

  // Sort: org admins first, then billing, then requesters, then by name.
  const sorted = [...contacts].sort((a, b) => {
    const score = (c: OrgContact) =>
      (c.isOrgAdmin ? 0 : 0) * 0 -
      (c.isOrgAdmin ? 100 : 0) -
      (c.isOrgBilling ? 10 : 0) -
      (c.isRequester ? 1 : 0);
    const sd = score(a) - score(b);
    if (sd !== 0) return sd;
    return (a.name ?? a.email).localeCompare(b.name ?? b.email);
  });

  return (
    <div>
      <label className="block text-xs font-medium text-[var(--color-header)]">{label}</label>
      <div className="mt-1 max-h-48 space-y-1 overflow-y-auto rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-2">
        {sorted.map((c) => {
          const key = c.email.toLowerCase();
          const checked = picked.has(key);
          return (
            <label
              key={c.id}
              className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-[var(--color-surface)]"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(c.email)}
                className="h-4 w-4 accent-[var(--color-t3-blue)]"
              />
              <span className="truncate">
                {c.name ? `${c.name} ` : ''}
                <span className="text-[var(--color-muted)]">&lt;{c.email}&gt;</span>
              </span>
              {c.isOrgAdmin && (
                <span className="ml-auto rounded-full bg-[var(--color-t3-blue)]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-t3-blue)]">
                  admin
                </span>
              )}
              {!c.isOrgAdmin && c.isOrgBilling && (
                <span className="ml-auto rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                  billing
                </span>
              )}
            </label>
          );
        })}
      </div>
      <p className="mt-1 text-xs text-[var(--color-muted)]">
        {picked.size} selected · org admins are pre-checked when scheduling.
      </p>
    </div>
  );
}
